/**
 * ReservationPaymentOrder — l'enllaç de pagament al popover de la reserva
 * (`plans/stripe-payment-links.md` §3.2 i §3.4, F5).
 *
 * - Sense enllaç obert: el botó «Crear enllaç de pagament». A petició i prou (decisió 1): res es
 *   crea sol.
 * - Amb un enllaç obert: import, data màxima, «Copiar enllaç», «WhatsApp» (el flux de missatges de
 *   sempre, plantilla `PAYMENT_LINK`: el sistema prepara el text i la persona l'envia), «Marca com
 *   enviat» (apunta la sol·licitud i també el senyal «Link enviat» de la targeta; és l'únic botó
 *   d'«enviat» mentre l'enllaç és obert) i «Detall» (línies, cobraments, cancel·lar). Avís vermell si ha passat la data màxima,
 *   si l'import o les dates ja no quadren amb la reserva (`check`, amb «Crea'n un de nou») o si
 *   està per revisar. «WhatsApp» no marca res com enviat: ho fa la persona amb el seu botó.
 * - Sense enllaç obert i amb l'últim caducat: «Enllaç caducat el …» i «Crea'n un de nou». Amb
 *   l'últim cobrat, retornat o en disputa: una línia amb l'estat i l'import, que obre el detall.
 *
 * Només ADMIN (decisió 15). La fila és compacta; tot el detall va a un diàleg.
 */

import { useState } from 'react';
import { AlertTriangle, Copy, Link2, MessageCircle } from 'lucide-react';
import type { PaymentOrderSentVia, PaymentOrderView } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePaymentOrderAction,
  usePaymentOrders,
  useRereadPaymentOrderProvider,
} from '@/hooks/usePaymentOrders';
import { useWhatsAppSendFlow } from '@/components/messaging/useWhatsAppSendFlow';
import { resolveQuickWhatsAppState } from '@/lib/messaging';
import {
  MARK_SENT_TITLE,
  SENT_VIAS,
  SENT_VIA_LABELS,
  formatCents,
  ORDER_STATUS_LABELS,
  formatShortDay,
  orderCheckProblem,
  orderRefundedCents,
  orderReviewReasons,
  orderWarning,
  pickDisplayOrder,
  reviewReasonText,
  sentViaLabel,
} from '@/lib/payment-orders';
import { CALENDAR_PALETTE } from '@/components/calendar-v2/calendar-theme';
import { CreatePaymentOrderDialog } from './CreatePaymentOrderDialog';
import { copyPaymentLink, rereadHandlerFor, shouldOfferProviderReread } from './payment-link-utils';
import { PaymentOrderDetailDialog } from './PaymentOrderDetailDialog';

const BUTTON_CLASS = 'h-6 px-1.5 text-[10px] gap-1';

// ─── La fila (sense hooks: els tests la criden com una funció) ───────────────

export interface PaymentOrderRowProps {
  /** L'enllaç que s'ensenya (`pickDisplayOrder`): l'obert o, si no n'hi ha, l'últim si és caducat,
   *  cobrat, retornat, en disputa o per revisar. `null`: cap. */
  order: PaymentOrderView | null;
  /** Es pot oferir «Crear enllaç» (hi ha pagament pendent i no el cobra una OTA). */
  canCreate: boolean;
  pending: boolean;
  /** Per què no es pot obrir WhatsApp («Sense telèfon»…); `null` si es pot. */
  whatsappDisabledReason: string | null;
  onCreate: () => void;
  onCopy: (url: string) => void;
  onWhatsapp: () => void;
  onMarkSent: (via: PaymentOrderSentVia) => void;
  onDetail: () => void;
  /**
   * «Torna a llegir Stripe»: rellegeix el pagament al proveïdor i apunta el que digui. Només
   * s'ensenya en un enllaç retingut o per revisar per la sessió (`shouldOfferProviderReread`).
   */
  onReread?: () => void;
}

/**
 * El botó discret de «Torna a llegir Stripe» (o res, si no toca). Una funció i no un component:
 * els tests recorren l'arbre d'elements de la fila sense muntar-la.
 */
function rereadButton(
  order: PaymentOrderView,
  pending: boolean,
  onReread: (() => void) | undefined,
) {
  if (!onReread || !shouldOfferProviderReread(order)) return null;
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn(BUTTON_CLASS, 'text-gray-600 underline-offset-2 hover:underline')}
      disabled={pending}
      onClick={onReread}
      title="Torna a preguntar a Stripe què ha passat amb aquest pagament i apunta el que digui. No canvia res a mà."
      aria-label="Torna a llegir Stripe"
    >
      Torna a llegir Stripe
    </Button>
  );
}

export function PaymentOrderRow({
  order,
  canCreate,
  pending,
  whatsappDisabledReason,
  onCreate,
  onCopy,
  onWhatsapp,
  onMarkSent,
  onDetail,
  onReread,
}: PaymentOrderRowProps) {
  if (!order) {
    if (!canCreate) return null;
    return (
      <div className="flex items-center gap-1 min-w-0" data-testid="reservation-payment-order">
        <Button
          size="sm"
          variant="outline"
          className={cn(BUTTON_CLASS, 'border-indigo-300 text-indigo-700 hover:bg-indigo-50')}
          disabled={pending}
          onClick={onCreate}
          title="Prepara un enllaç perquè l'hoste pagui amb targeta. No s'envia sol."
          aria-label="Crear enllaç de pagament"
        >
          <Link2 className="h-3 w-3" aria-hidden="true" />
          Crear enllaç de pagament
        </Button>
      </div>
    );
  }

  // Un enllaç que ja no és obert (caducat, cobrat, retornat, en disputa, o tancat «per revisar»):
  // una sola línia amb el que cal saber i, si toca, tornar-lo a demanar. El detall, al diàleg.
  if (order.status !== 'OPEN') {
    const expired = order.status === 'EXPIRED';
    const refunded = orderRefundedCents(order);
    const expiredDay = formatShortDay(order.expiredAt ?? order.dueAt);
    const review = order.needsReviewAt ? reviewReasonText(orderReviewReasons(order)) : null;
    return (
      <div
        className={cn(
          'flex flex-col gap-1 rounded border px-2 py-1',
          review ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-gray-50/60',
        )}
        data-testid="reservation-payment-order"
        data-order-status={order.status}
      >
        <div className="flex flex-wrap items-center gap-1">
          <p className="min-w-0 flex-1 text-[11px] text-gray-800">
            {expired ? (
              <span className="font-medium" style={{ color: CALENDAR_PALETTE.popoverLabel }}>
                Enllaç caducat{expiredDay ? ` el ${expiredDay}` : ''}
              </span>
            ) : (
              <>
                <span className="font-medium" style={{ color: CALENDAR_PALETTE.popoverLabel }}>
                  Enllaç de pagament:
                </span>{' '}
                <span className="font-semibold">{ORDER_STATUS_LABELS[order.status] ?? order.status}</span>
              </>
            )}
            {' · '}
            {formatCents(order.amountCents)}
            {refunded > 0 ? ` (retornat ${formatCents(refunded)})` : ''}
            {order.testMode && (
              <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
                PROVA
              </span>
            )}
          </p>
          {canCreate && (
            <Button
              size="sm"
              variant="outline"
              className={cn(BUTTON_CLASS, 'border-indigo-300 text-indigo-700 hover:bg-indigo-50')}
              disabled={pending}
              onClick={onCreate}
              title="Prepara un enllaç nou perquè l'hoste pagui amb targeta. No s'envia sol."
              aria-label="Crea'n un de nou"
            >
              <Link2 className="h-3 w-3" aria-hidden="true" />
              Crea'n un de nou
            </Button>
          )}
          {rereadButton(order, pending, onReread)}
          <Button
            size="sm"
            variant="ghost"
            className={BUTTON_CLASS}
            onClick={onDetail}
            aria-label="Detall de l'enllaç de pagament"
          >
            Detall
          </Button>
        </div>
        {review && (
          <p
            role="alert"
            className="flex items-start gap-1 text-[10px] text-red-800"
          >
            <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="break-words">Per revisar: {review}.</span>
          </p>
        )}
      </div>
    );
  }

  const warning = orderWarning(order);
  const mismatch = orderCheckProblem(order) !== null;
  return (
    <div
      className="flex flex-col gap-1 rounded border border-indigo-200 bg-indigo-50/50 px-2 py-1.5"
      data-testid="reservation-payment-order"
      data-past-due={order.pastDue ? 'true' : undefined}
      data-check-failed={mismatch ? 'true' : undefined}
    >
      <p className="text-[11px] text-gray-800">
        <span className="font-medium" style={{ color: CALENDAR_PALETTE.popoverLabel }}>
          Enllaç de pagament:
        </span>{' '}
        <span className="font-semibold">{formatCents(order.amountCents)}</span>
        {order.dueAt ? ` · fins al ${formatShortDay(order.dueAt)}` : ' · sense data'}
        {order.testMode && (
          <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-900">
            PROVA
          </span>
        )}
      </p>

      {warning && (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded border border-red-200 bg-red-50 px-1.5 py-1 text-[10px] text-red-800"
        >
          <p className="flex items-start gap-1">
            <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="break-words">{warning}</span>
          </p>
          {mismatch && (
            <div>
              <Button
                size="sm"
                variant="outline"
                className="h-5 border-red-300 bg-white px-1.5 text-[10px] text-red-800 hover:bg-red-100"
                disabled={pending}
                onClick={onCreate}
                title="Crea un enllaç amb l'import d'ara; aquest deixa de valer"
                aria-label="Crea'n un de nou"
              >
                Crea'n un de nou
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Enllaç de pagament">
        <Button
          size="sm"
          variant="outline"
          className={BUTTON_CLASS}
          onClick={() => onCopy(order.payUrl)}
          aria-label="Copiar enllaç de pagament"
        >
          <Copy className="h-3 w-3" aria-hidden="true" />
          Copiar enllaç
        </Button>
        <span className="inline-flex" title={whatsappDisabledReason ?? "Preparar el missatge amb l'enllaç i obrir WhatsApp"}>
          <Button
            size="sm"
            variant="outline"
            className={cn(BUTTON_CLASS, 'border-green-300 text-green-700 hover:bg-green-50')}
            disabled={pending || !!whatsappDisabledReason || order.pastDue}
            onClick={onWhatsapp}
            aria-label="Enviar l'enllaç de pagament per WhatsApp"
          >
            <MessageCircle className="h-3 w-3" aria-hidden="true" />
            WhatsApp
          </Button>
        </span>
        {rereadButton(order, pending, onReread)}
        <Button
          size="sm"
          variant="ghost"
          className={cn(BUTTON_CLASS, 'ml-auto')}
          onClick={onDetail}
          aria-label="Detall de l'enllaç de pagament"
        >
          Detall
        </Button>
      </div>

      {order.sentAt ? (
        <p className="text-[10px] text-gray-600">
          Enviat el {formatShortDay(order.sentAt)}
          {order.sentVia ? ` · ${sentViaLabel(order.sentVia)}` : ''}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Marca com enviat">
          <span className="text-[10px] font-medium" style={{ color: CALENDAR_PALETTE.popoverLabel }}>
            Marca com enviat:
          </span>
          {SENT_VIAS.map((via) => (
            <Button
              key={via}
              size="sm"
              variant="outline"
              className="h-5 px-1.5 text-[10px]"
              disabled={pending}
              onClick={() => onMarkSent(via)}
              title={MARK_SENT_TITLE}
              aria-label={`Marca com enviat per ${SENT_VIA_LABELS[via]}`}
            >
              {SENT_VIA_LABELS[via]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Amb hooks ───────────────────────────────────────────────────────────────

export interface ReservationPaymentOrderProps {
  reservationId: string;
  /** Hi ha pagament pendent i no el cobra una OTA: es pot oferir «Crear enllaç». */
  canCreate: boolean;
  phone?: string | null;
  phoneE164?: string | null;
}

function PaymentOrderBlock({
  reservationId,
  canCreate,
  phone,
  phoneE164,
}: ReservationPaymentOrderProps) {
  const [dialog, setDialog] = useState<'create' | 'detail' | null>(null);
  const { data: orders } = usePaymentOrders(reservationId);
  const action = usePaymentOrderAction();
  const reread = useRereadPaymentOrderProvider();
  const order = pickDisplayOrder(orders);
  // Només un enllaç obert es pot marcar com enviat.
  const orderId = order?.status === 'OPEN' ? order.id : null;

  const markSent = (via: PaymentOrderSentVia) => {
    if (orderId) action.mutate({ orderId, action: { kind: 'mark-sent', via } });
  };

  // El flux de missatges de sempre: crea la tasca `PAYMENT_LINK` (el backend hi posa l'enllaç de
  // la sol·licitud oberta), ensenya el text i obre WhatsApp. Qui envia és la persona, i qui diu que
  // s'ha enviat, també: «enviat» no es pot desfer a la sol·licitud, així que el flux NO el marca sol.
  // Queden els botons «Marca com enviat».
  const whatsapp = useWhatsAppSendFlow({
    reservationId,
    messageKind: 'PAYMENT_LINK',
    title: 'Enllaç de pagament',
  });
  const { disabledReason } = resolveQuickWhatsAppState({ phone, phoneE164 });

  return (
    <>
      <PaymentOrderRow
        order={order}
        canCreate={canCreate}
        pending={action.isPending || whatsapp.isCreating || reread.isPending}
        whatsappDisabledReason={disabledReason}
        onReread={rereadHandlerFor(order, reread.mutate)}
        onCreate={() => setDialog('create')}
        onCopy={(url) => void copyPaymentLink(url)}
        onWhatsapp={() => void whatsapp.start()}
        onMarkSent={markSent}
        onDetail={() => setDialog('detail')}
      />
      {whatsapp.dialogs}
      <CreatePaymentOrderDialog
        reservationId={reservationId}
        open={dialog === 'create'}
        onOpenChange={(open) => setDialog(open ? 'create' : null)}
      />
      {order && (
        <PaymentOrderDetailDialog
          open={dialog === 'detail'}
          onOpenChange={(open) => setDialog(open ? 'detail' : null)}
          order={order}
          pending={action.isPending}
          onCopy={(url) => void copyPaymentLink(url)}
          onMarkSent={markSent}
          onReplace={() => setDialog('create')}
          canReplace={order.status === 'OPEN' || canCreate}
          onCancel={(reason) =>
            action.mutate(
              { orderId: order.id, action: { kind: 'cancel', reason } },
              { onSuccess: () => setDialog(null) },
            )
          }
          onSupersede={() =>
            action.mutate(
              { orderId: order.id, action: { kind: 'supersede' } },
              { onSuccess: () => setDialog(null) },
            )
          }
        />
      )}
    </>
  );
}

/** L'enllaç de pagament d'una reserva. Qui no és ADMIN no en veu res (ni es fa cap consulta). */
export function ReservationPaymentOrder(props: ReservationPaymentOrderProps) {
  const { user } = useAuth();
  if (user?.role !== 'ADMIN') return null;
  return <PaymentOrderBlock {...props} />;
}

export default ReservationPaymentOrder;
